/**
 * m3 echo Frontend - Cutoffs Tab
 *
 * Admin-only tab for viewing cutoff/truncation logs.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  TablePagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  cutoffLogsApi,
  CutoffLogItem,
  CutoffLogsParams,
} from '../../services/admin';
import CutoffDetailDialog from './CutoffDetailDialog';

// ===========================
// Constants
// ===========================

const LAYER_OPTIONS = ['L3', 'L5', 'L6', 'zyte', 'grounding'];

// ===========================
// Helper Functions
// ===========================

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '-';
  const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
  return new Date(utcDateString).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getLayerColor = (layer: string): 'primary' | 'secondary' | 'warning' | 'info' | 'error' => {
  switch (layer) {
    case 'L3': return 'primary';
    case 'L5': return 'secondary';
    case 'L6': return 'warning';
    case 'zyte': return 'info';
    case 'grounding': return 'error';
    default: return 'primary';
  }
};

// ===========================
// Component
// ===========================

export const CutoffsTab: React.FC = () => {
  // Data state
  const [logs, setLogs] = useState<CutoffLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [layer, setLayer] = useState('');
  const [jobId, setJobId] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Detail dialog
  const [selectedItem, setSelectedItem] = useState<CutoffLogItem | null>(null);

  // ===========================
  // Data Loading
  // ===========================

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: CutoffLogsParams = {
        page: page + 1, // API uses 1-based pagination
        page_size: pageSize,
      };
      if (layer) params.layer = layer;
      if (jobId) params.job_id = Number(jobId);

      const response = await cutoffLogsApi.getLogs(params);
      setLogs(response.data.items);
      setTotal(response.data.total);
    } catch (err: any) {
      console.error('Error loading cutoff logs:', err);
      setError(err.message || 'Fehler beim Laden der Cutoff-Logs');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, layer, jobId]);

  // ===========================
  // Effects
  // ===========================

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // ===========================
  // Handlers
  // ===========================

  const handleResetFilters = () => {
    setLayer('');
    setJobId('');
    setPage(0);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ===========================
  // Render
  // ===========================

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Cutoff-Logs</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadLogs}
          disabled={loading}
        >
          Aktualisieren
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Filter
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Layer"
                value={layer}
                onChange={(e) => { setLayer(e.target.value); setPage(0); }}
              >
                <MenuItem value="">Alle Layer</MenuItem>
                {LAYER_OPTIONS.map(l => (
                  <MenuItem key={l} value={l}>{l}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Job ID"
                type="number"
                value={jobId}
                onChange={(e) => { setJobId(e.target.value); setPage(0); }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button size="small" onClick={handleResetFilters}>
                Zurücksetzen
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading && logs.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : logs.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Noch keine Cutoff-Logs vorhanden.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Zeitpunkt</TableCell>
                      <TableCell>Layer</TableCell>
                      <TableCell>Feld</TableCell>
                      <TableCell align="right">Limit</TableCell>
                      <TableCell align="right">Original</TableCell>
                      <TableCell align="right">Verloren</TableCell>
                      <TableCell align="right">Job</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow
                        key={log.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelectedItem(log)}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(log.created_at)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.layer}
                            color={getLayerColor(log.layer)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{log.field_name}</TableCell>
                        <TableCell align="right">
                          {log.cutoff_limit.toLocaleString('de-DE')}
                        </TableCell>
                        <TableCell align="right">
                          {log.original_length.toLocaleString('de-DE')}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>
                          {log.chars_lost.toLocaleString('de-DE')}
                        </TableCell>
                        <TableCell align="right">
                          {log.job_id ?? '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handlePageSizeChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Pro Seite:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <CutoffDetailDialog
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        item={selectedItem}
      />
    </Box>
  );
};

export default CutoffsTab;
